/* eslint-disable no-extra-semi */
import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { MatStepper } from '@angular/material/stepper'
import { GlobalMessageType, RoutingService } from '@spartacus/core'
import { BehaviorSubject, Observable, Subscription, forkJoin } from 'rxjs'
import { debounceTime, distinctUntilChanged, map, mergeAll, mergeMap, switchMap, tap, toArray } from 'rxjs/operators'
import {
  AgreementDetailEntry,
  AgreementTypeProducts,
  DispenserAgreementUpdatePayload,
  EndCustomerInformation,
  Entry,
  Product,
  ProductsEntry,
  ProductsFromEntries,
  RequesterInformation
} from '../../../../../models/dispensers.model'
import { CustomRoutes } from '../../../../../models/enum/custom-routes.enum'
import { DispenserType } from '../../../../../models/enum/dispenser.enum'
import { SharedValue } from '../../../../../models/enum/shared.enum'
import { DispenserAgreementService } from '../../../../../services/agreement/dispenser/dispenser-agreement.service'
import { CustomGlobalMessageService } from '../../../../../services/shared/custom-global-message/custom-global-message.service'
import { SharedService } from '../../../../../services/shared/shared.service'
import { CustomTableColumnType } from '../../../../shared/custom-components/custom-table/modals/enum/custom-table.enum'
import {
  ICustomTableColumn,
  ICustomTableHeader
} from '../../../../shared/custom-components/custom-table/modals/interface/custom-table.interface'

@Component({
  selector: 'app-update-dispenser-agreement',
  templateUrl: './update-dispenser-agreement.component.html',
  styleUrls: ['./update-dispenser-agreement.component.scss'],
  standalone: false
})
export class UpdateDispenserAgreementComponent implements OnInit, AfterViewInit {
  @ViewChild('stepper') stepper: MatStepper
  assetSelectionFormGroup: FormGroup
  termInformationFormGroup: FormGroup

  subscription = new Subscription()
  isLoading$ = new BehaviorSubject(true)
  requestError$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)

  isReviewing: boolean = false
  agreementCode: string
  agreementFolType: string
  DispenserTypeEnum = DispenserType
  errorMessage: string

  initalCategoryProducts: Map<string, ProductsFromEntries[]> = new Map<string, ProductsFromEntries[]>()
  categoryProducts: Map<string, ProductsFromEntries[]> = new Map<string, ProductsFromEntries[]>()
  requesterInformation: RequesterInformation
  endCustomerInformation: EndCustomerInformation

  public headers: ICustomTableHeader[] = [
    { title: 'dispenserAgreement.item', cssStyle: 'min-width:200px' },
    { title: 'dispenserAgreement.itemNumber' },
    { title: 'dispenserAgreement.colour' },
    { title: 'dispenserAgreement.system' },
    { title: 'dispenserAgreement.systemType' },
    { title: 'dispenserAgreement.quantity', type: CustomTableColumnType.INPUT }
  ]
  public columns: ICustomTableColumn[] = [
    { key: 'name', cssStyle: 'min-width:200px' },
    { key: 'code' },
    { key: 'colour' },
    { key: 'system' },
    { key: 'systemType' },
    {
      key: 'quantity',
      type: CustomTableColumnType.INPUT,
      input: { type: 'text', value: 'quantity', translate: true, bindInput: true }
    }
  ]

  constructor(
    private formBuilder: FormBuilder,
    public dispenserAgreementService: DispenserAgreementService,
    private changeDetectorRef: ChangeDetectorRef,
    public routingService: RoutingService,
    public globalMessageService: CustomGlobalMessageService,
    private sharedService: SharedService
  ) {
    this.assetSelectionFormGroup = this.formBuilder.group({
      searchText: ['']
    })
    this.termInformationFormGroup = this.formBuilder.group({
      accept: [false, Validators.required],
      comments: ['']
    })

    const sharedService$ = this.sharedService.getUrlParam('code').subscribe((code) => (this.agreementCode = code))
    this.subscription.add(sharedService$)
  }

  ngOnInit(): void {
    //map products from dispenser and agreement details
    this.getProductMap()

    //get requester information
    this.getRequesterInformation()

    //get endcustomer information
    this.getEndCustomerInformation()
  }

  ngAfterViewInit() {
    const searchAssets$ = this.assetSelectionFormGroup.controls.searchText.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((searchText) => {
        this.searchAssets(searchText?.toLowerCase())
      })

    this.subscription.add(searchAssets$)
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe()
  }

  //#region Asset Selection
  private getProductMap() {
    const torkAndLibraDispenserCategories$: Observable<AgreementTypeProducts[]> =
      this.getTorkAndLibraDispenserProducts()
    const agreementDetailEntries$: Observable<Entry[]> = this.getAgreementDetails()
    const productMapping$ = forkJoin([torkAndLibraDispenserCategories$, agreementDetailEntries$])
      .pipe(
        map(([categories, entries]) => {
          let folType: string

          //find and set foltype
          if (categories.length > 0) {
            for (const category of categories.values()) {
              for (const entry of entries.values()) {
                //define FolType base on user selected products
                const hasFoundCategory = category.products.some((product) => product.code === entry.code)

                if (hasFoundCategory) {
                  folType = category.folType
                  this.setAgreementFolType(category.folType)
                }
              }
            }

            for (const category of categories.values()) {
              if (category.folType === folType) {
                //set products with quantity from entries
                if (entries.length > 0) {
                  for (const entry of entries.values()) {
                    const foundProductIndex = category.products.findIndex((product) => product.code === entry.code)

                    if (foundProductIndex >= 0) {
                      ;(category.products[foundProductIndex] as ProductsFromEntries).quantity = entry.quantity
                      ;(category.products[foundProductIndex] as ProductsFromEntries).newProduct = false
                    }
                  }
                }

                //set map with updated products with quantity/newproduct fields
                this.initalCategoryProducts.set(category.categoryName, category.products as ProductsFromEntries[])
              }
            }
          }

          //use initialCategoryProducts to reset search
          this.categoryProducts = this.initalCategoryProducts
        })
      )
      .subscribe({
        error: () => this.handleGenericRequestError(),
        complete: () => this.isLoading$.next(false)
      })

    this.subscription.add(productMapping$)
  }

  private getTorkAndLibraDispenserProducts() {
    const torkDispenserCategories$ = this.dispenserAgreementService.getDispenserCategories('tork')
    const libraDispenserCategories$ = this.dispenserAgreementService.getDispenserCategories('libra')

    return forkJoin([torkDispenserCategories$, libraDispenserCategories$]).pipe(
      switchMap(([torkDispenserCategories, libraDispenserCategories]) => {
        if (torkDispenserCategories?.values) {
          torkDispenserCategories?.values.forEach((value) => (value.folType = 'tork'))
        }
        if (libraDispenserCategories?.values) {
          libraDispenserCategories?.values.forEach((value) => (value.folType = 'libra'))
        }

        const torkDispenserCategoriesValues = torkDispenserCategories?.values ?? []
        const libraDispenserCategoriesValues = libraDispenserCategories?.values ?? []

        return [...torkDispenserCategoriesValues, ...libraDispenserCategoriesValues]
      }),
      mergeMap((category) => {
        const products$ = this.getCategoryProducts(category.query.query.value)

        return forkJoin([products$]).pipe(
          mergeAll(),
          map((products) => ({ folType: category.folType, categoryName: category.name, products: products }))
        )
      }),
      toArray(),
      tap({
        error: () => this.handleGenericRequestError()
      })
    )
  }

  private getCategoryProducts(queryString): Observable<Product[]> {
    return this.dispenserAgreementService.getCategoryProducts({ queryString: queryString }).pipe(
      map((products) =>
        products.map(
          (product) =>
            ({
              code: product.code,
              colour: product.colour,
              name: product.name,
              summary: product.summary,
              system: product.refillID,
              systemType: product.systemType
            }) as Product
        )
      )
    )
  }

  private getAgreementDetails(): Observable<Entry[]> {
    return this.dispenserAgreementService.getAgreementDetailEntries(this.agreementCode).pipe(
      map((agreementDetailEntries) =>
        (agreementDetailEntries.results as Array<AgreementDetailEntry>).map(
          (entry) =>
            ({
              code: entry.product?.code,
              name: entry.product?.name,
              colour: entry.product?.colour,
              quantity: entry.quantity
            }) as Entry
        )
      ),
      tap({
        error: () => this.handleGenericRequestError()
      })
    )
  }

  updateProductQuantity(producToUpdate: ProductsFromEntries, category: string): void {
    this.categoryProducts.get(category).forEach((product) => {
      if (product.code === producToUpdate.code) {
        product.quantity = producToUpdate.quantity
      }
    })
  }
  //#endregion

  //#region Terms & Conditions
  public setAgreementFolType(folType: string): void {
    const isTork = folType === 'tork'
    this.agreementFolType = isTork ? 'TORK_LIBRA_DISPENSER' : 'LIBRA_PERIOD_POSITIVITY_PROGRAM'
  }

  public get disableTermInformation(): boolean {
    return !this.termInformationFormGroup?.value?.accept
  }
  // #endregion

  //#region Review
  private getRequesterInformation(): void {
    const requesterInformation$ = this.sharedService
      .getUser()
      .pipe(
        map((user) => ({
          abn: user?.orgUnit?.abn,
          adressLine1: user?.defaultAddress?.line1,
          adressLine2: user?.defaultAddress?.line2,
          distributionCompany: user?.orgUnit?.name,
          email: user?.defaultAddress?.email,
          phoneNumber: user?.defaultAddress?.phone,
          postalCode: user?.defaultAddress?.postalCode,
          requesterName: user?.name,
          state: user?.defaultAddress?.town,
          uid: user?.uid
        }))
      )
      .subscribe((requesterInformation) => (this.requesterInformation = requesterInformation))

    this.subscription.add(requesterInformation$)
  }

  private getEndCustomerInformation(): void {
    const endCustomerAddress$ = this.dispenserAgreementService.getEndCustomerAddress(this.agreementCode)
    const agreementDetail$ = this.dispenserAgreementService.getAgreementDetail(this.agreementCode)

    const endCustomerInformation$ = forkJoin([endCustomerAddress$, agreementDetail$])
      .pipe(
        map(([endCustomerAddress, agreementDetail]) => ({
          businessName: agreementDetail.endCustomer.orgUnit?.businessNumber,
          adressLine1: endCustomerAddress.line1,
          adressLine2: endCustomerAddress.line2,
          contactName: agreementDetail.endCustomer.orgUnit.name,
          email: endCustomerAddress.email,
          postCode: endCustomerAddress.postalCode,
          phoneNumber: endCustomerAddress.phone,
          state: endCustomerAddress.town,
          installer: agreementDetail.installer,
          installReason: agreementDetail.installReason
        }))
      )
      .subscribe((endCustomerInformation) => (this.endCustomerInformation = endCustomerInformation))

    this.subscription.add(endCustomerInformation$)
  }

  submitDispenserAgreementUpdate(): void {
    this.isLoading$.next(true)

    const payload: DispenserAgreementUpdatePayload = {
      agreementTypes: {
        agreementType: this.agreementFolType,
        agreementNumber: this.agreementCode,
        products: this.updatedProducts
      }
    }

    const submitDispenserAgreementUpdate$ = this.dispenserAgreementService
      .updateDispenserAgreement(this.requesterInformation.uid, this.agreementCode, payload)
      .pipe(tap(() => this.isLoading$.next(true)))
      .subscribe({
        error: () => this.handleGenericRequestError(),
        complete: () => {
          this.isLoading$.next(false)
          this.isReviewing = true
        }
      })

    this.subscription.add(submitDispenserAgreementUpdate$)
  }

  private get updatedProducts(): ProductsEntry[] {
    const updatedProducts: ProductsEntry[] = []

    this.categoryProducts.forEach((category) =>
      (category as ProductsFromEntries[]).forEach((product) => {
        if (product.quantity) {
          updatedProducts.push({
            productCode: product.code,
            quantity: product.quantity,
            newProduct: product.newProduct ?? true
          })
        }
      })
    )

    return updatedProducts
  }
  //#endregion

  //#region Search Assets
  searchAssets(searchText: string): void {
    const categorySearchProducts: Map<string, ProductsFromEntries[]> = new Map<string, ProductsFromEntries[]>()
    this.errorMessage = null

    if (searchText) {
      this.initalCategoryProducts.forEach((products: ProductsFromEntries[], category: string) => {
        const searchedProducts: ProductsFromEntries[] = []

        products.forEach((product) => {
          if (product.name?.toLowerCase().includes(searchText) || product.code?.toLowerCase().includes(searchText)) {
            searchedProducts.push(product)
          }
        })
        if (searchedProducts.length > 0) {
          categorySearchProducts.set(category, searchedProducts)
        }
      })
      if (categorySearchProducts.size > 0) {
        this.categoryProducts = categorySearchProducts
      } else {
        this.categoryProducts = new Map<string, ProductsFromEntries[]>()
        this.errorMessage = SharedValue.errorProuctNotFound
      }
    } else {
      this.categoryProducts = this.initalCategoryProducts
    }

    this.changeDetectorRef.detectChanges()
  }

  getSearchText(): FormControl {
    return this.assetSelectionFormGroup.get('searchText') as FormControl
  }
  //#endregion searchAssets

  get disableAssetSelectionNextStep(): boolean {
    return !this.categoryProducts.size
  }

  private handleGenericRequestError() {
    this.isLoading$.next(false)
    this.globalMessageService.add({ key: 'shared.error.noSimulation' }, GlobalMessageType.MSG_TYPE_ERROR)
    this.requestError$.next(true)
  }

  backToHistory(): void {
    this.routingService.go({ cxRoute: CustomRoutes.DISPENSER_AGREEMENT_HISTORY })
  }
}
